import { useEffect } from 'react';
import { Card, ProgressBar, ListGroup, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './CourseSidebar.css';

/**
 * Course Progress Sidebar Component
 * Displays course progress and section navigation
 * 
 * Props:
 *  - course: Course object with sections
 *  - progress: Progress object with completedSections and sectionProgress
 *  - currentSection: Currently active section
 *  - isCollapsed: Whether sidebar is collapsed
 *  - onToggleCollapse: Callback when collapse toggle is clicked
 */
export function CourseSidebar({ course, progress, currentSection, isCollapsed = false, onToggleCollapse, onSectionSelect = null }) {
  // Force component re-render when progress changes for proper syncing
  useEffect(() => {
    // Dependency on progress changes ensures sidebar updates when progress is updated
  }, [progress?.completedSections?.length, progress?.sectionProgress]);

  if (!course || !progress) {
    return null;
  }

  const courseId = course?.id;

  const completedSections = progress?.completedSections || [];
  const totalSections = course.sections?.length || 0;
  
  // Calculate overall progress based on average of all section scores/completion
  let overallProgress = 0;
  if (totalSections > 0) {
    const sections = course.sections || [];
    let totalScore = 0;
    
    sections.forEach(section => {
      const sectionData = progress?.sectionProgress?.[section.id];
      if (sectionData) {
        // Use score if available, fallback to percent_complete
        const score = sectionData.score || sectionData.percent_complete || 0;
        // Handle both 0-1 and 0-100 ranges
        totalScore += score <= 1 ? score * 100 : score;
      }
    });
    
    overallProgress = Math.round(totalScore / totalSections);
  }
  
  const completionPercentage = overallProgress;

  /**
   * Get status badge for a section
   */
  const getSectionStatus = (sectionId) => {
    if (currentSection?.id === sectionId) {
      return { label: 'Current', className: 'section-current', icon: '📍' };
    }

    // F6 (PRD): Locked sections are not clickable and shown with lock icon
    const isUnlocked = progress?.isSectionUnlocked
      ? progress.isSectionUnlocked(sectionId)
      : true; // Fallback: all sections accessible if method unavailable
    if (!isUnlocked) {
      return { label: 'Locked', className: 'section-locked', icon: '🔒' };
    }

    // F5/F6 (PRD): Mastered (95%+) and Learned (85%+) take precedence over generic Completed
    const sectionProgressData = progress?.sectionProgress?.[sectionId];
    const rawScore = sectionProgressData?.score || 0;
    const normalizedScore = rawScore <= 1 ? rawScore * 100 : rawScore;

    if (normalizedScore >= 95) {
      return { label: 'Mastered', className: 'section-mastered', icon: '🏆' };
    }
    if (normalizedScore >= 85) {
      return { label: 'Learned', className: 'section-learned', icon: '✨' };
    }

    if (completedSections.includes(sectionId)) {
      return { label: 'Completed', className: 'section-completed', icon: '✅' };
    }

    // Check if section has been visited/studied (has progress data)
    const hasProgress = sectionProgressData && (
      (sectionProgressData.questionsAttempted || 0) > 0 ||
      normalizedScore > 0 ||
      sectionProgressData.status === 'in_progress' ||
      (sectionProgressData.percent_complete && sectionProgressData.percent_complete > 0)
    );

    if (hasProgress) {
      return { label: 'In Progress', className: 'section-in-progress', icon: '⏳' };
    }

    return { label: 'Not Started', className: 'section-not-started', icon: '⭕' };
  };

  /**
   * Get section progress score - handles both 0-1 and 0-100 ranges
   */
  const getSectionProgress = (sectionId) => {
    const sectionProgress = progress?.sectionProgress?.[sectionId];
    if (!sectionProgress?.score) return 0;
    const score = sectionProgress.score;
    return score <= 1 ? Math.round(score * 100) : Math.round(score);
  };

  /**
   * Get section completion percentage - handles both 0-1 and 0-100 ranges
   * Falls back to score if percent_complete is not available
   */
  const getSectionPercentComplete = (sectionId) => {
    const sectionProgress = progress?.sectionProgress?.[sectionId];
    if (!sectionProgress) return 0;
    
    // Try percent_complete first
    if (sectionProgress.percent_complete) {
      const percent = sectionProgress.percent_complete;
      return percent <= 1 ? Math.round(percent * 100) : Math.round(percent);
    }
    
    // Fallback to score if percent_complete is not set
    if (sectionProgress.score) {
      const score = sectionProgress.score;
      return score <= 1 ? Math.round(score * 100) : Math.round(score);
    }
    
    return 0;
  };

  const sections = course.sections || [];

  return (
    <div className="course-sidebar-wrapper">
      <Button
        as={Link}
        to={`/course/${courseId}`}
        variant="outline-secondary"
        size="sm"
        className="w-100 mb-2"
        title="Return to course dashboard"
      >
        ← Back to Course
      </Button>

      {isCollapsed ? (
        <div className="course-sidebar course-sidebar-collapsed">
          <Button
            variant="light"
            className="sidebar-toggle-button"
            onClick={onToggleCollapse}
            title="Expand sidebar"
            aria-label="Expand progress sidebar"
          >
            ▶
          </Button>
        </div>
      ) : (
        <div className="course-sidebar course-sidebar-expanded">
          <div className="sidebar-header">
            <Button
              variant="ghost"
              size="sm"
              className="sidebar-toggle-button"
              onClick={onToggleCollapse}
              title="Collapse sidebar"
              aria-label="Collapse progress sidebar"
            >
              ◀
            </Button>
          </div>

          <Card className="sidebar-card mb-3">
            <Card.Body className="p-3">
              <div className="progress-stats mb-2">
                  <div className="stat-item">
                  <span className="stat-label">Progress</span>
                  <span className="stat-value">{completionPercentage}%</span>
                </div>
              </div>

              <ProgressBar 
                now={completionPercentage} 
                label={`${completionPercentage}%`}
                className="course-progress-bar"
              />
            </Card.Body>
          </Card>

          <Card className="sidebar-card">
            <ListGroup variant="flush" className="sections-list">
              {sections.map((section) => {
                const status = getSectionStatus(section.id);
                const percentComplete = getSectionPercentComplete(section.id);
                const isLocked = status.className === 'section-locked';
                const isClickable = !!onSectionSelect && !isLocked;

                return (
                  <ListGroup.Item
                    key={section.id}
                    className={`section-item ${status.className}`}
                    action={isClickable}
                    onClick={isClickable ? () => onSectionSelect(section.id) : undefined}
                    style={{
                      cursor: isLocked ? 'not-allowed' : isClickable ? 'pointer' : 'default',
                      opacity: isLocked ? 0.55 : 1,
                    }}
                    title={isLocked ? 'Complete the previous section to unlock' : section.title}
                  >
                    <div className="section-header">
                      <div className="section-title">
                        <div className="section-info">
                          <div className="section-name">
                            <span className="section-icon me-1" aria-hidden="true">{status.icon}</span>
                            <span className="section-title-text">{section.title}</span>
                          </div>
                          <small className="section-status">{status.label}</small>
                        </div>
                      </div>
                      <div className="section-percent">{percentComplete}%</div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Card>
        </div>
      )}
    </div>
  );
}
